<script setup lang="ts">
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'
import { ref, onMounted, watch, defineExpose, computed } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface Option {
  id: number
  value: string | number
  text: string
  selected: boolean
  element?: HTMLElement
}

interface Props {
  options: Option[]
  errorText?: string
}

const props = defineProps<Props>()

const emit = defineEmits(['update:selectedValues'])

const rootRef = ref<HTMLElement | null>(null)
const options = ref<Option[]>([])
const selected = ref<number[]>([])
const show = ref(false)
const searchOption = ref<string>('')
const inputRef = ref<HTMLInputElement | null>(null)

const open = () => {
  show.value = true
}

const close = () => {
  show.value = false
}

const isOpen = () => show.value === true

onClickOutside(rootRef, () => {
  close()
})

onMounted(() => {
  loadOptions()
})

const loadOptions = () => {
  options.value = props.options.map((option, index) => ({
    ...option,
    id: option.id || index,
    selected: option.selected || false,
  }))
  selected.value = options.value
    .map((option, index) => (option.selected ? index : -1))
    .filter((index) => index !== -1)
}

watch(() => props.options, loadOptions, { deep: true })

const select = (filteredIndex: number, event: MouseEvent) => {
  const selectedOption = filteredOptions.value[filteredIndex]
  const index = options.value.findIndex((option) => option.id === selectedOption.id)

  const newOptions = [...options.value]
  if (!newOptions[index].selected) {
    newOptions[index] = {
      ...newOptions[index],
      selected: true,
      element: event.target as HTMLElement,
    }
    selected.value = [...selected.value, index]
  } else {
    newOptions[index] = { ...newOptions[index], selected: false }
    selected.value = selected.value.filter((i) => i !== index)
  }
  options.value = newOptions
  emitSelectedValues()
  searchOption.value = ''
  inputRef.value?.focus()
}

const remove = (id: number | string) => {
  const index = options.value.findIndex((option) => option.id === id)
  if (index === -1) return

  const newOptions = [...options.value]
  newOptions[index] = { ...newOptions[index], selected: false }
  options.value = newOptions
  selected.value = selected.value.filter((i) => i !== index)
  emitSelectedValues()
}

const selectedValues = () => {
  return selected.value.map((optionIndex) => options.value[optionIndex].value)
}

const emitSelectedValues = () => {
  emit('update:selectedValues', selectedValues())
}

const clearSelection = () => {
  options.value = options.value.map((option) => ({
    ...option,
    selected: false,
  }))
  selected.value = []
  emitSelectedValues()
}

const filteredOptions = computed(() => {
  return options.value.filter((option) =>
    matchesSearchAccentInsensitive(option.text, searchOption.value),
  )
})

const selectedOptions = computed(() => {
  return options.value.filter((option) => option.selected)
})

const inputPlaceholder = computed(() =>
  selectedOptions.value.length > 0 ? '' : t('placeholders.select'),
)

function handleFocus() {
  open()
}

function handleContainerClick() {
  open()
  inputRef.value?.focus()
}

function toggleDropdown(event: MouseEvent) {
  event.stopPropagation()
  if (show.value) {
    close()
    inputRef.value?.blur()
    return
  }
  open()
  inputRef.value?.focus()
}

defineExpose({ clearSelection })
</script>

<template>
  <div ref="rootRef" class="relative w-full" :class="show ? 'z-50' : 'z-10'">
    <div
      class="flex w-full cursor-text flex-wrap items-center gap-2 rounded-none border border-zinc-300 bg-white py-2 pl-3 pr-10 transition-colors hover:border-zinc-400 focus-within:border-primary"
      @click="handleContainerClick">
      <div
        v-for="option in selectedOptions"
        :key="option.id"
        class="inline-flex max-w-full items-center gap-1 rounded-none border border-blue-100 bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700">
        <span class="max-w-[12rem] truncate">{{ option.text }}</span>
        <button
          type="button"
          class="shrink-0 rounded-none text-blue-700 hover:text-red"
          :aria-label="t('globals.multiselect.remove_option', { name: option.text })"
          @click.stop="remove(option.id)">
          <svg
            class="fill-current"
            role="presentation"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M9.35355 3.35355C9.54882 3.15829 9.54882 2.84171 9.35355 2.64645C9.15829 2.45118 8.84171 2.45118 8.64645 2.64645L6 5.29289L3.35355 2.64645C3.15829 2.45118 2.84171 2.45118 2.64645 2.64645C2.45118 2.84171 2.45118 3.15829 2.64645 3.35355L5.29289 6L2.64645 8.64645C2.45118 8.84171 2.45118 9.15829 2.64645 9.35355C2.84171 9.54882 3.15829 9.54882 3.35355 9.35355L6 6.70711L8.64645 9.35355C8.84171 9.54882 9.15829 9.54882 9.35355 9.35355C9.54882 9.15829 9.54882 8.84171 9.35355 8.64645L6.70711 6L9.35355 3.35355Z"
              fill="currentColor"></path>
          </svg>
        </button>
      </div>

      <input
        ref="inputRef"
        v-model="searchOption"
        type="text"
        :placeholder="inputPlaceholder"
        class="min-w-[8rem] flex-1 border-0 bg-transparent py-1 text-sm text-gray-900 outline-none ring-0 placeholder:text-gray-400 focus:border-0 focus:outline-none focus:ring-0"
        @focus="handleFocus"
        @click.stop="handleFocus" />
    </div>

    <button
      type="button"
      class="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-none text-zinc-400 hover:text-zinc-600"
      :aria-expanded="show"
      :aria-label="show ? t('globals.multiselect.close') : t('globals.multiselect.open')"
      @click="toggleDropdown">
      <span class="block h-5 w-5 transition-transform" :class="isOpen() ? 'rotate-180' : ''">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
            fill="currentColor"></path>
        </svg>
      </span>
    </button>

    <div
      v-show="isOpen()"
      class="absolute left-0 top-full z-50 mt-1 max-h-96 w-full overflow-y-auto rounded-none border border-zinc-200 bg-white shadow-lg">
      <div class="flex w-full flex-col">
        <div
          v-if="filteredOptions.length === 0"
          class="px-3 py-2 text-sm text-gray">
          {{ t('placeholders.empty_list') }}
        </div>
        <div
          v-for="(option, index) in filteredOptions"
          :key="option.id"
          class="cursor-pointer border-b border-stroke px-3 py-2 font-medium transition-colors duration-300 hover:bg-lightGray"
          :class="option.selected ? 'border-l-2 border-primary bg-blue-50' : 'border-l-2 border-transparent'"
          @click="select(index, $event)">
          {{ option.text }}
        </div>
      </div>
    </div>

    <p v-if="errorText" class="mt-1 text-sm text-red">
      {{ errorText }}
    </p>
  </div>
</template>
